import React, { useState, useEffect } from 'react';
import { TimelineNode } from '../App';
import Icon from './Icon';
import './DetailPanel.css';

/**
 * DetailPanel 组件
 * 
 * 功能：
 * - 显示时间轴节点的详细信息
 * - 支持图片、视频、音频附件展示
 * - 显示距离该节点的时间差（年月日时分秒）
 * - 点击外部区域或按 ESC 键关闭
 * 
 * 特性：
 * - 全屏模态展示
 * - 动画过渡效果
 * - 附件标题显示
 * - 实时时间差更新
 */

interface DetailPanelProps {
  node: TimelineNode; // 当前显示的节点
  onClose: () => void; // 关闭回调
  canEdit?: boolean; // 是否允许编辑
  onEdit?: (node: TimelineNode) => void; // 编辑回调
  permission?: 'full' | 'view' | null; // 权限类型
}

/**
 * 计算时间差
 * 
 * @param dateStr 日期字符串（格式：YYYY-MM-DD HH:mm:ss）
 * @returns 时间差对象（年、月、日、时、分、秒）
 */
const calculateTimeDiff = (dateStr: string): {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} => {
  const now = new Date();
  const targetDate = new Date(dateStr);
  
  // 计算时间差（毫秒）
  const diffMs = now.getTime() - targetDate.getTime();
  
  // 转换为正数（如果目标是未来时间，也显示正数）
  const diff = Math.abs(diffMs);
  
  // 计算年、月、日
  const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));
  const months = Math.floor((diff % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
  const days = Math.floor((diff % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { years, months, days, hours, minutes, seconds };
};

/**
 * 格式化时间差文本
 * 
 * @param timeDiff 时间差对象
 * @returns 格式化的时间差字符串
 */
const formatTimeDiff = (timeDiff: {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}): string => {
  // 计算总天数
  const totalDays = timeDiff.years * 365 + timeDiff.months * 30 + timeDiff.days;
  
  // 格式化详细时间（只显示时分秒）
  const parts: string[] = [];
  if (timeDiff.hours > 0) parts.push(`${timeDiff.hours}小时`);
  if (timeDiff.minutes > 0) parts.push(`${timeDiff.minutes}分`);
  if (timeDiff.seconds > 0) parts.push(`${timeDiff.seconds}秒`);
  
  return `${totalDays}天 (${parts.join('')})`;
};

const DetailPanel: React.FC<DetailPanelProps> = ({ node, onClose, canEdit = false, onEdit, permission = null }) => {
  const [timeDiff, setTimeDiff] = useState(calculateTimeDiff(node.date));
  const audioRef = React.useRef<HTMLAudioElement>(null);

  /**
   * 每秒更新时间差
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeDiff(calculateTimeDiff(node.date));
    }, 1000);

    return () => clearInterval(interval);
  }, [node.date]);

  /**
   * 音频自动播放和清理
   */
  useEffect(() => {
    // 检查是否有音频附件
    const hasAudio = node.attachments?.some(att => att.type === 'audio');
    
    if (hasAudio && audioRef.current) {
      // 自动播放音频
      audioRef.current.play().catch(err => {
        console.log('音频自动播放失败（可能需要用户交互）:', err);
      });
    }

    // 组件卸载或节点切换时暂停音频
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [node.id, node.attachments]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      // 关闭时暂停音频
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      onClose();
    }
  };

  return (
    <div className="detail-panel-overlay" onClick={handleOverlayClick}>
      <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="detail-header">
          <div className="detail-icon">
            <Icon type={node.icon} />
          </div>
          <div className="detail-title-section">
            <div className="detail-title">{node.title}</div>
            <div className="detail-date">{node.date}</div>
          </div>
          <div className="detail-header-actions">
            {canEdit && onEdit && (
              <button 
                className="detail-edit" 
                onClick={() => onEdit(node)}
                title="编辑节点"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </button>
            )}
            <button 
              className="detail-close" 
              onClick={() => {
                // 关闭时暂停音频
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                }
                onClose();
              }}
            >×</button>
          </div>
        </div>
        
        <div className="detail-body">
          {/* 时间差显示 */}
          <div className="detail-time-diff">
            距此刻已经 {formatTimeDiff(timeDiff)}
          </div>
          
          {permission === 'full' && (
            <>
              <div className="detail-description">{node.description}</div>
              
              {node.attachments && node.attachments.length > 0 && (
                <div className="detail-attachments">
                  {node.attachments.map((attachment, index) => (
                    <div key={index} className="detail-attachment">
                      {attachment.type === 'image' && (
                        <img src={attachment.url} alt={attachment.caption || node.title} />
                      )}
                      {attachment.type === 'video' && (
                        <video controls>
                          <source src={attachment.url} />
                        </video>
                      )}
                      {attachment.type === 'audio' && (
                        <audio 
                          ref={index === 0 ? audioRef : undefined}
                          controls
                        >
                          <source src={attachment.url} />
                        </audio>
                      )}
                      {attachment.caption && (
                        <div className="detail-attachment-caption">{attachment.caption}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailPanel;